import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  IconButton,
  Button,
  Chip,
  Stack,
  Sheet,
  Slider,
  Card,
  AspectRatio,
  Tooltip,
} from '@mui/joy';
import {
  X as CloseIcon,
  Plus as AddIcon,
  Trash as DeleteIcon,
  Image as ImageIcon,
  Sparkle as AIIcon,
  ArrowsClockwise as RefreshIcon,
  HandTap as TouchAppIcon,
  TextT as FormatColorTextIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { MediaPicker } from './MediaPicker';
import { ColorPicker } from './ColorPicker';
import type { SectionData, SectionDefinition, SectionField } from '../../types/visualEditor';

interface PropertyPanelProps {
  section: SectionData | null;
  sectionConfig: SectionDefinition | null;
  sectionIndex: number | null;
  onChange: (fieldName: string, value: unknown) => void;
  onVariantChange?: (variant: string) => void;
  onClose?: () => void;
  siteId?: number | string;
  aiSettings?: Record<string, unknown>;
  contentBrief?: string;
  brandColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
}

export const PropertyPanel: React.FC<PropertyPanelProps> = ({
  section,
  sectionConfig,
  sectionIndex,
  onChange,
  onVariantChange,
  onClose,
  siteId,
  aiSettings,
  contentBrief,
  brandColors,
}) => {
  const [mediaPickerField, setMediaPickerField] = useState<string | null>(null);
  const [colorPickerField, setColorPickerField] = useState<string | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['content']);

  // AI field generation
  const generateFieldMutation = useMutation({
    mutationFn: async ({ fieldName }: { fieldName: string }) => {
      return api.post('/api/cms/ai/generate-field/', {
        section_type: section?.type,
        field_name: fieldName,
        current_data: section?.data,
        ai_settings: aiSettings,
        content_brief: contentBrief,
      });
    },
    onSuccess: (response: any, { fieldName }: { fieldName: string }) => {
      const data = response?.data || response;
      if (data?.value) {
        onChange(fieldName, data.value);
      }
    },
  });

  const handleGenerateField = async (fieldName: string) => {
    setGenerating(fieldName);
    try {
      await generateFieldMutation.mutateAsync({ fieldName });
    } finally {
      setGenerating(null);
    }
  };

  const handleArrayItemAdd = (fieldName: string) => {
    const currentArray = (section?.data?.[fieldName] as string[]) || [];
    onChange(fieldName, [...currentArray, '']);
  };

  const handleArrayItemChange = (fieldName: string, index: number, value: string) => {
    const currentArray = [...((section?.data?.[fieldName] as string[]) || [])];
    currentArray[index] = value;
    onChange(fieldName, currentArray);
  };

  const handleArrayItemDelete = (fieldName: string, index: number) => {
    const currentArray = [...((section?.data?.[fieldName] as string[]) || [])];
    currentArray.splice(index, 1);
    onChange(fieldName, currentArray);
  };

  // Empty state - no section selected
  if (sectionIndex === null || !section || !sectionConfig) {
    return (
      <Sheet
        variant="outlined"
        sx={{
          width: 350,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.surface',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'neutral.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <TouchAppIcon size={40} color="var(--joy-palette-neutral-400)" />
          </Box>
          <Typography level="title-md" sx={{ mb: 1 }}>
            Select a Section
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Click on a section in the preview to edit its properties
          </Typography>
        </Box>
      </Sheet>
    );
  }

  const renderField = (fieldName: string, field: SectionField) => {
    const value = section.data?.[fieldName] ?? '';

    switch (field.type) {
      case 'text':
        const textColorKey = `${fieldName}_color`;
        const textColorValue = section.data?.[textColorKey] as string | undefined;
        return (
          <FormControl key={fieldName}>
            <FormLabel>
              {field.label}
              {field.max && (
                <Chip size="sm" sx={{ ml: 1 }}>
                  {String(value).length}/{field.max}
                </Chip>
              )}
            </FormLabel>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Input
                value={String(value)}
                onChange={(e) => onChange(fieldName, e.target.value)}
                placeholder={field.placeholder}
                size="sm"
                sx={{ flex: 1 }}
              />
              <Tooltip title={textColorValue ? `Text color: ${textColorValue}` : 'Set text color'}>
                <IconButton
                  size="sm"
                  onClick={() => setColorPickerField(colorPickerField === textColorKey ? null : textColorKey)}
                  variant={textColorValue ? 'soft' : 'plain'}
                  sx={{
                    '--IconButton-size': '32px',
                  }}
                >
                  <FormatColorTextIcon
                    size={18}
                    color={textColorValue || 'inherit'}
                  />
                </IconButton>
              </Tooltip>
              {field.ai_enabled && (
                <Tooltip title={value ? 'Regenerate' : 'Generate with AI'}>
                  <IconButton
                    size="sm"
                    onClick={() => handleGenerateField(fieldName)}
                    loading={generating === fieldName}
                    color="primary"
                    variant={value ? 'outlined' : 'solid'}
                  >
                    {value ? <RefreshIcon size={18} /> : <AIIcon size={18} />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {colorPickerField === textColorKey && (
              <Box sx={{ mt: 1 }}>
                <ColorPicker
                  value={textColorValue || ''}
                  onChange={(color) => onChange(textColorKey, color)}
                  label="Text Color"
                  helpText="Override the default text color for this field"
                  brandColors={brandColors || {
                    primary: '#2563EB',
                    secondary: '#7C3AED',
                  }}
                />
                {textColorValue && (
                  <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => {
                      onChange(textColorKey, null);
                      setColorPickerField(null);
                    }}
                    sx={{ mt: 0.5 }}
                  >
                    Clear color override
                  </Button>
                )}
              </Box>
            )}
            {field.help_text && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                {field.help_text}
              </Typography>
            )}
          </FormControl>
        );

      case 'textarea':
      case 'richtext':
        const areaColorKey = `${fieldName}_color`;
        const areaColorValue = section.data?.[areaColorKey] as string | undefined;
        return (
          <FormControl key={fieldName}>
            <FormLabel>
              {field.label}
              {field.max && (
                <Chip size="sm" sx={{ ml: 1 }}>
                  {String(value).length}/{field.max}
                </Chip>
              )}
            </FormLabel>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
              <Textarea
                value={String(value)}
                onChange={(e) => onChange(fieldName, e.target.value)}
                placeholder={field.placeholder}
                minRows={3}
                size="sm"
                sx={{ flex: 1 }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Tooltip title={areaColorValue ? `Text color: ${areaColorValue}` : 'Set text color'}>
                  <IconButton
                    size="sm"
                    onClick={() => setColorPickerField(colorPickerField === areaColorKey ? null : areaColorKey)}
                    variant={areaColorValue ? 'soft' : 'plain'}
                    sx={{
                      '--IconButton-size': '32px',
                    }}
                  >
                    <FormatColorTextIcon
                      size={18}
                      color={areaColorValue || 'inherit'}
                    />
                  </IconButton>
                </Tooltip>
                {field.ai_enabled && (
                  <Tooltip title={value ? 'Regenerate' : 'Generate with AI'}>
                    <IconButton
                      size="sm"
                      onClick={() => handleGenerateField(fieldName)}
                      loading={generating === fieldName}
                      color="primary"
                      variant={value ? 'outlined' : 'solid'}
                    >
                      {value ? <RefreshIcon size={18} /> : <AIIcon size={18} />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            {colorPickerField === areaColorKey && (
              <Box sx={{ mt: 1 }}>
                <ColorPicker
                  value={areaColorValue || ''}
                  onChange={(color) => onChange(areaColorKey, color)}
                  label="Text Color"
                  helpText="Override the default text color for this field"
                  brandColors={brandColors || {
                    primary: '#2563EB',
                    secondary: '#7C3AED',
                  }}
                />
                {areaColorValue && (
                  <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => {
                      onChange(areaColorKey, null);
                      setColorPickerField(null);
                    }}
                    sx={{ mt: 0.5 }}
                  >
                    Clear color override
                  </Button>
                )}
              </Box>
            )}
            {field.help_text && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                {field.help_text}
              </Typography>
            )}
          </FormControl>
        );

      case 'url':
        return (
          <FormControl key={fieldName}>
            <FormLabel>{field.label}</FormLabel>
            <Input
              value={String(value)}
              onChange={(e) => onChange(fieldName, e.target.value)}
              placeholder="https://..."
              type="url"
              size="sm"
            />
          </FormControl>
        );

      case 'select':
        return (
          <FormControl key={fieldName}>
            <FormLabel>{field.label}</FormLabel>
            <Select
              value={String(value)}
              onChange={(_, newValue) => onChange(fieldName, newValue)}
              size="sm"
            >
              {field.options?.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
            {field.help_text && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                {field.help_text}
              </Typography>
            )}
          </FormControl>
        );

      case 'array':
        const arrayValues = (section.data?.[fieldName] as string[]) || [];
        return (
          <FormControl key={fieldName}>
            <FormLabel>
              {field.label}
              <Button
                size="sm"
                variant="plain"
                startDecorator={<AddIcon size={18} />}
                sx={{ ml: 1 }}
                onClick={() => handleArrayItemAdd(fieldName)}
              >
                Add
              </Button>
            </FormLabel>
            <Stack spacing={0.5}>
              {arrayValues.map((item: string, index: number) => (
                <Box key={index} sx={{ display: 'flex', gap: 0.5 }}>
                  <Input
                    value={item}
                    onChange={(e) => handleArrayItemChange(fieldName, index, e.target.value)}
                    placeholder={`Item ${index + 1}`}
                    size="sm"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="sm"
                    color="danger"
                    variant="plain"
                    onClick={() => handleArrayItemDelete(fieldName, index)}
                  >
                    <DeleteIcon size={18} />
                  </IconButton>
                </Box>
              ))}
              {arrayValues.length === 0 && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  No items added yet
                </Typography>
              )}
            </Stack>
          </FormControl>
        );

      case 'media':
      case 'image':
        return (
          <FormControl key={fieldName}>
            <FormLabel>{field.label}</FormLabel>
            {value && (
              <Card variant="outlined" sx={{ mb: 1, position: 'relative' }}>
                <AspectRatio ratio="16/9">
                  <img
                    src={String(value)}
                    alt={field.label}
                    style={{ objectFit: 'cover' }}
                  />
                </AspectRatio>
                <IconButton
                  size="sm"
                  variant="solid"
                  color="danger"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    '--IconButton-size': '24px',
                  }}
                  onClick={() => onChange(fieldName, '')}
                >
                  <CloseIcon size={16} />
                </IconButton>
              </Card>
            )}
            <Input
              value={String(value)}
              onChange={(e) => onChange(fieldName, e.target.value)}
              placeholder="Select from media library"
              size="sm"
              endDecorator={
                <IconButton
                  size="sm"
                  onClick={() => setMediaPickerField(fieldName)}
                  disabled={!siteId}
                >
                  <ImageIcon size={18} />
                </IconButton>
              }
            />
          </FormControl>
        );

      case 'color':
        return (
          <ColorPicker
            key={fieldName}
            value={String(value)}
            onChange={(color) => onChange(fieldName, color)}
            label={field.label}
            helpText={field.help_text}
            brandColors={brandColors || {
              primary: '#2563EB',
              secondary: '#7C3AED',
            }}
          />
        );

      case 'range':
        return (
          <FormControl key={fieldName}>
            <FormLabel>
              {field.label}
              <Chip size="sm" sx={{ ml: 1 }}>
                {String(value ?? '')}
              </Chip>
            </FormLabel>
            <Slider
              value={Number(value) || field.min || 0}
              onChange={(_, newValue) => onChange(fieldName, newValue)}
              min={field.min || 0}
              max={field.max || 100}
              size="sm"
            />
          </FormControl>
        );

      case 'boolean':
        return (
          <FormControl key={fieldName}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange(fieldName, e.target.checked)}
              />
              <FormLabel sx={{ mb: 0 }}>{field.label}</FormLabel>
            </Box>
            {field.help_text && (
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                {field.help_text}
              </Typography>
            )}
          </FormControl>
        );

      default:
        return (
          <FormControl key={fieldName}>
            <FormLabel>{field.label}</FormLabel>
            <Input
              value={String(value)}
              onChange={(e) => onChange(fieldName, e.target.value)}
              size="sm"
            />
          </FormControl>
        );
    }
  };

  // Group fields by category (if they have one) or put in "content" group
  const groupedFields: Record<string, Array<[string, SectionField]>> = {};
  Object.entries(sectionConfig.fields || {}).forEach(([name, field]) => {
    // Skip fields with show_if conditions that aren't met (simplified check)
    const group = 'content'; // Could categorize by field.type or add a category property
    if (!groupedFields[group]) {
      groupedFields[group] = [];
    }
    groupedFields[group].push([name, field]);
  });

  return (
    <Sheet
      variant="outlined"
      sx={{
        width: 350,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.surface',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography level="title-md">
            {sectionConfig.label}
          </Typography>
          {section.variant && section.variant !== 'default' && (
            <Chip size="sm" variant="soft" sx={{ mt: 0.5 }}>
              {section.variant}
            </Chip>
          )}
        </Box>
        {onClose && (
          <IconButton size="sm" variant="plain" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Variant Selector (if section has multiple variants) */}
      {sectionConfig.variants && Object.keys(sectionConfig.variants).length > 1 && (
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <FormControl size="sm">
            <FormLabel>Variant</FormLabel>
            <Select
              value={section.variant || 'default'}
              onChange={(_, value) => value && onVariantChange?.(value)}
              size="sm"
            >
              {Object.entries(sectionConfig.variants).map(([key, variant]) => (
                <Option key={key} value={key}>
                  {variant.label}
                </Option>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Fields */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Stack spacing={2}>
          {Object.entries(groupedFields).map(([group, fields]) => (
            <Box key={group}>
              {fields.map(([name, field]) => renderField(name, field))}
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Media Picker Modal */}
      {mediaPickerField && siteId && (
        <MediaPicker
          open={!!mediaPickerField}
          onClose={() => setMediaPickerField(null)}
          onSelect={(media) => {
            onChange(mediaPickerField, media.url);
            setMediaPickerField(null);
          }}
          siteId={Number(siteId)}
        />
      )}
    </Sheet>
  );
};
